import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import productService from "../../db/product-service";
import cartService from "../../db/cart-service";
import authService from "../../services/auth-service";
import organicIcon from "../../organicIcon.png";
import CategoryFilter from "../CategoryFilter/CategoryFilter";

import { initializeDatabase, countryCurrencyMap } from "../../db/init-data";
import { currencySymbols, formatPrice, detectUserCountry } from "../../utils/currency";
import { getProductContent } from "../../utils/productContentData";

import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CardMedia,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";

export const ProductListCustomer = (props) => {
  const navigate = useNavigate();
  const [productList, setProductList] = useState([]);
  const [debugInfo, setDebugInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userCountry, setUserCountry] = useState('US');
  const isMobile = useMediaQuery("(max-width:768px)");
  const [isRequestInProgress, setIsRequestInProgress] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null
  });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [customerId, setCustomerId] = useState(sessionStorage.getItem("customerId"));
  const hasFetchedRef = useRef(false);
  const isFetchingRef = useRef(false);

  // Product category mapping
  const getProductCategory = (product) => {
    const name = product.name.toLowerCase();
    if (name.includes('glucose guard')) return 'metabolic';
    if (name.includes('ruby cube') || name.includes('berry banana')) return 'cardiovascular';
    if (name.includes('golden cube') || name.includes('green power') || name.includes('fireball')) return 'immunity';
    if (name.includes('gut') || name.includes('digestive')) return 'digestive';
    if (name.includes('detox') || name.includes('liver')) return 'detox';
    return 'immunity'; // Default for smoothies and shots
  };

  // Currency symbols imported from utility

  // Country names for dropdown
  const countryNames = {
    'US': 'United States',
    'IN': 'India',
    'GB': 'United Kingdom',
    'EU': 'Europe',
    'JP': 'Japan'
  };

  useEffect(() => {
    const initCountry = async () => {
      try {
        const savedCountry = sessionStorage.getItem('userCountry');
        if (savedCountry) {
          setUserCountry(savedCountry);
        } else {
          const country = await detectUserCountry();
          setUserCountry(country);
          sessionStorage.setItem('userCountry', country);
        }
      } catch (error) {
        console.warn('Error detecting country, using default:', error);
        setUserCountry('US');
      }
    };
    initCountry();
  }, []);

  useEffect(() => {
    if (userCountry && !hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchData();
    }
  }, [userCountry]);

  // Re-fetch only when country is explicitly changed by user
  const handleCountryChange = (event) => {
    const newCountry = event.target.value;
    setUserCountry(newCountry);
    sessionStorage.setItem('userCountry', newCountry);
    hasFetchedRef.current = false;
  };

  const initializeProducts = async () => {
    try {
      setLoading(true);
      await initializeDatabase();
      setConfirmDialog({
        open: true,
        title: "Success",
        message: "Sample products initialized!",
        onConfirm: () => {
          setConfirmDialog({ ...confirmDialog, open: false });
          fetchData();
        }
      });
    } catch (err) {
      console.error("Error initializing products:", err);
      setConfirmDialog({
        open: true,
        title: "Error",
        message: "Error initializing products: " + err.message,
        onConfirm: () => setConfirmDialog({ ...confirmDialog, open: false })
      });
    } finally {
      setLoading(false);
    }
  };

  // Get stock for current country
  const getCountryStock = (product) => {
    // If product is inactive, return 0 stock
    if (product.active === false) {
      return 0;
    }
    
    if (product.countryStock && product.countryStock[userCountry] !== undefined) {
      return product.countryStock[userCountry];
    }
    
    // If no stock for current country, check if there's stock for any country
    if (product.countryStock) {
      const firstCountry = Object.keys(product.countryStock)[0];
      if (firstCountry && product.countryStock[firstCountry] !== undefined) {
        return product.countryStock[firstCountry];
      }
    }
    
    return 0;
  };

  const fetchData = async (retryCount = 0) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsRequestInProgress(true);
    setLoading(true);
    setError(null);
    
    const startTime = performance.now();
    console.log(`[PRODUCTS] Fetch started - Attempt ${retryCount + 1}, Country: ${userCountry}`);
    
    try {
      console.log('[PRODUCTS] Calling productService.getAllProducts()...');
      const products = await productService.getAllProducts(true);
      const fetchTime = (performance.now() - startTime).toFixed(2);
      console.log(`[PRODUCTS] Fetch completed in ${fetchTime}ms, Total products: ${products?.length || 0}`);
      
      // Handle case where products is undefined or null
      if (!products || !Array.isArray(products)) {
        console.warn('[PRODUCTS] ERROR: Products returned undefined or not an array', { products });
        setProductList([]);
        setLoading(false);
        setIsRequestInProgress(false);
        return;
      }
      
      console.log(`[PRODUCTS] Filtering products for country: ${userCountry}`);
      const availableProducts = products.filter(product => {
        const isAvailable = !product.availability || 
                           (Array.isArray(product.availability) && 
                            product.availability.includes(userCountry));
        
        const hasPricing = !!product.pricing && Object.keys(product.pricing).length > 0;
        const isActive = product.active !== false;
        
        if (!isAvailable) console.log(`[PRODUCTS] Filtered out ${product.name}: not available in ${userCountry}`);
        if (!hasPricing) console.log(`[PRODUCTS] Filtered out ${product.name}: no pricing`);
        if (!isActive) console.log(`[PRODUCTS] Filtered out ${product.name}: inactive`);
        
        return isAvailable && hasPricing && isActive;
      }).slice(0, 50);
      
      console.log(`[PRODUCTS] Available products after filtering: ${availableProducts.length}`);
      
      availableProducts.forEach((product) => {
        product.quantity = product.active !== false ? 1 : 0;
        if (!product.category) {
          product.category = getProductCategory(product);
        }
      });
      
      console.log(`[PRODUCTS] Successfully loaded ${availableProducts.length} products`);
      setProductList(availableProducts);
      setLoading(false);
      setIsRequestInProgress(false);
      isFetchingRef.current = false;
    } catch (err) {
      const errorTime = (performance.now() - startTime).toFixed(2);
      console.error(`[PRODUCTS] ERROR after ${errorTime}ms:`, {
        message: err.message,
        stack: err.stack,
        attempt: retryCount + 1
      });
      
      if (retryCount < 2) {
        console.log(`[PRODUCTS] Retrying in ${3 + retryCount * 2} seconds...`);
        setError(null);
        setTimeout(() => {
          setIsRequestInProgress(false);
          isFetchingRef.current = false;
          fetchData(retryCount + 1);
        }, (3 + retryCount * 2) * 1000);
        return;
      }
      
      console.error('[PRODUCTS] All retry attempts failed');
      setError(err.message || 'Failed to load products. Please check your internet connection and refresh the page.');
      setLoading(false);
      setIsRequestInProgress(false);
      isFetchingRef.current = false;
    }
  };


  // This function is no longer used as we're directly displaying the price with currency symbol

  const showToast = (message, type = 'success') => {
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      bottom: 20px;
      right: 20px;
      padding: 12px 20px;
      background-color: ${type === 'success' ? '#4caf50' : '#f44336'};
      color: white;
      border-radius: 4px;
      z-index: 9999;
      font-family: Arial, sans-serif;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
    `;
    toast.textContent = message;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      if (document.body.contains(toast)) {
        document.body.removeChild(toast);
      }
    }, 3000);
  };



  if (loading) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '40px',
        minHeight: '300px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{ 
          fontSize: '3rem', 
          marginBottom: '20px',
          animation: 'pulse 1.5s ease-in-out infinite'
        }}>
          🌱
        </div>
        <div style={{ fontSize: '1.2rem', color: '#2e7d32', marginBottom: '10px', fontWeight: '600' }}>
          Loading fresh products...
        </div>
        <div style={{ fontSize: '0.9rem', color: '#666' }}>
          This may take a moment on slower connections
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.1); opacity: 0.8; }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <h2 style={{ color: error.includes('Still loading') ? '#2e7d32' : '#d32f2f' }}>
          {error.includes('Still loading') ? '⏳ Loading...' : '⚠️ Error'}
        </h2>
        <p>{error}</p>
        {!error.includes('Still loading') && (
          <button 
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              backgroundColor: '#2e7d32',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '1rem'
            }}
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  // Check if the user is sendvijay21@gmail.com
  const userEmail = sessionStorage.getItem("customerEmail");
  const showCountryDropdown = userEmail?.toLowerCase() === "sendvijay21@gmail.com" || userEmail?.toLowerCase() === "organicgreenshub.hyderabad@gmail.com";

  return (
    <div>
      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {confirmDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={confirmDialog.onConfirm} color="primary" autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
      

      
      {/* Products Section */}
      <div>
        <div>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3 }}>
            {showCountryDropdown && (
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel id="country-select-label">Country</InputLabel>
                <Select
                  labelId="country-select-label"
                  id="country-select"
                  value={userCountry}
                  label="Country"
                  onChange={handleCountryChange}
                >
                  {Object.keys(countryNames).map(code => (
                    <MenuItem key={code} value={code}>
                      {countryNames[code]} ({currencySymbols[code]})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
          
          {productList.length === 0 ? (
            <div>
              <p>No products available in {countryNames[userCountry]}.</p>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                <button onClick={fetchData}>Refresh</button>
              </div>
            </div>
          ) : (
            <>
              <CategoryFilter 
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
              />
              <Grid container spacing={2} justifyContent="flex-start">
                {productList
                  .filter(product => {
                    if (selectedCategory === 'all') return true;
                    if (Array.isArray(product.category)) {
                      return product.category.includes(selectedCategory);
                    }
                    return product.category === selectedCategory;
                  })
                  .map((product) => {
                const stock = getCountryStock(product);
                const isInactive = product.active === false;
                const currencyCode = countryCurrencyMap[userCountry];
                
                if (!product.pricing || !product.pricing[currencyCode]) return null;
                
                return (
                  <Grid
                    item
                    key={product._id}
                    xs={12}
                    sm={6}
                    md={3}
                    lg={3}
                    xl={3}
                  >
                    <Card
                      onClick={() => navigate(`/product/${product._id}`)}
                      sx={{
                        height: "100%",
                        boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                        borderRadius: 4,
                        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                        position: "relative",
                        overflow: "hidden",
                        cursor: 'pointer',
                        "&:hover": { 
                          transform: "translateY(-12px) scale(1.02)",
                          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
                          "& .product-image": {
                            transform: "scale(1.1)"
                          }
                        },
                        opacity: isInactive ? 0.8 : 1,
                        background: "linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)",
                        border: "1px solid rgba(0,0,0,0.05)"
                      }}
                    >
                      {product.image_url && (
                        <Box sx={{ width: '100%', aspectRatio: '16/9', overflow: 'hidden', background: '#f5f5f5' }}>
                          <CardMedia
                            component="img"
                            image={product.image_url}
                            alt={product.name}
                            loading="lazy"
                            className="product-image"
                            sx={{ 
                              width: '100%',
                              height: '100%',
                              cursor: 'pointer',
                              transition: 'transform 0.4s ease',
                              objectFit: 'contain'
                            }}
                          />
                        </Box>
                      )}

                      {product.is_organic === 1 && (
                        <div
                          style={{
                            position: "absolute",
                            top: 8,
                            left: 8,
                            background: "linear-gradient(135deg, #2e7d32, #1b5e20)",
                            color: "white",
                            padding: "4px 8px",
                            fontSize: "11px",
                            borderRadius: "12px",
                            fontWeight: "600",
                            zIndex: 2,
                            display: "flex",
                            alignItems: "center",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                          }}
                        >
                          <img
                            src={organicIcon}
                            alt="Organic"
                            style={{
                              height: "16px",
                              width: "16px",
                              marginRight: "4px",
                            }}
                          />
                          Organic
                        </div>
                      )}

                      {isInactive && (
                        <div
                          style={{
                            position: "absolute",
                            top: 5,
                            right: 5,
                            backgroundColor: "#d32f2f",
                            color: "white",
                            padding: "2px 6px",
                            fontSize: "12px",
                            borderRadius: "4px",
                            fontWeight: "bold",
                            zIndex: 1,
                          }}
                        >
                          Out of Stock
                        </div>
                      )}

                      <CardContent sx={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        p: 1.5,
                        gap: 1,
                        background: 'rgba(255,255,255,0.95)',
                        backdropFilter: 'blur(10px)',
                        borderTop: '1px solid rgba(0,0,0,0.05)'
                      }}>
                        <Typography variant="subtitle1" fontWeight="600" sx={{ 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          color: '#1a1a1a',
                          lineHeight: 1.3
                        }}>
                          {product.name}
                        </Typography>

                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '6px',
                          paddingTop: '0',
                          borderTop: '2px solid rgba(46, 125, 50, 0.15)'
                        }}>
                          {[product.cardBenefit, product.cardPackageInfo, product.cardKeyFeatures].filter(Boolean).map((bullet, idx) => (
                            <div key={idx} style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '11px',
                              fontWeight: '700',
                              color: '#1b5e20',
                              letterSpacing: '0.4px',
                              textTransform: 'uppercase'
                            }}>
                              <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '16px',
                                height: '16px',
                                background: 'linear-gradient(135deg, #2e7d32, #1b5e20)',
                                borderRadius: '50%',
                                color: 'white',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                flexShrink: 0,
                                boxShadow: '0 2px 4px rgba(46, 125, 50, 0.3)'
                              }}>✓</span>
                              {bullet}
                            </div>
                          ))}
                        </div>
                        
                        <Typography variant="body2" color="primary" sx={{
                          fontWeight: '600',
                          fontSize: '1.1rem',
                          color: '#2e7d32'
                        }}>
                          {formatPrice(product.pricing[countryCurrencyMap[userCountry]], userCountry)} / {product.unit || 'kg'}
                        </Typography>

                        <Button
                          fullWidth
                          variant="contained"
                          sx={{
                            background: isInactive ? "#9e9e9e" : "linear-gradient(135deg, #2e7d32, #1b5e20)",
                            color: "white",
                            fontWeight: "600",
                            borderRadius: "8px",
                            padding: "10px",
                            textTransform: "none",
                            boxShadow: "0 4px 12px rgba(46, 125, 50, 0.3)",
                            "&:hover": { 
                              background: isInactive ? "#9e9e9e" : "linear-gradient(135deg, #1b5e20, #0d4e14)",
                              transform: "translateY(-2px)",
                              boxShadow: "0 6px 16px rgba(46, 125, 50, 0.4)"
                            },
                            "&:disabled": {
                              background: "#e0e0e0",
                              color: "#9e9e9e"
                            }
                          }}
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!isInactive) {
                              const userId = authService.getCurrentUserId();
                              if (!userId) {
                                showToast('Please log in to add items to cart', 'error');
                                navigate('/login');
                                return;
                              }
                              try {
                                const cart = await cartService.getCart(userId);
                                const isPresent = cart.some(item => item.productId === product._id);
                                await cartService.addToCart(userId, product._id, 1, isPresent);
                                showToast(`${product.name} added to cart!`);
                              } catch (error) {
                                console.error('Error adding to cart:', error);
                                showToast(error.message || 'Failed to add item to cart', 'error');
                              }
                            }
                          }}
                          disabled={isInactive}
                        >
                          {isInactive ? "Out of Stock" : "Add to cart"}
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductListCustomer;
